import { useState, useEffect, useCallback, Fragment } from 'react';
import { adminApi } from '../lib/api';

// ── Receipts (AMEX expense reconciliation) ──
//
// The owner's monthly "are we clean?" cockpit. Field staff capture
// AMEX-charged receipts (photo + amount + reason) via the receipts.
// silverservice.com surface; here the owner imports the month's AMEX CSV and
// reconciles the two sides. Flags surfaced per charge:
//   MISSING_RECEIPT — a charge with no photo
//   NO_REASON       — a matched receipt with a blank reason
//   LARGE           — a single charge >= the anomaly threshold ($500 default)
//   DUPLICATE       — same amount + date as another charge
//   MERCHANT_SPIKE  — way above this merchant's usual spend (needs history)
//
// All matching/flagging is computed server-side (lib/receipts.js); this view is
// read + a few owner fix-ups (set a reason, manually attach a receipt). No SOS
// writes happen here — expenses are the company's own books, not customer data.
const FLAG_META = {
  MISSING_RECEIPT: { label: 'Missing receipt', color: '#c62828' },
  NO_REASON:       { label: 'No reason',       color: '#b26500' },
  LARGE:           { label: 'Over threshold',  color: '#d84315' },
  DUPLICATE:       { label: 'Duplicate',       color: '#8e24aa' },
  MERCHANT_SPIKE:  { label: 'Merchant spike',  color: '#ef6c00' },
  ORPHAN_RECEIPT:  { label: 'No matching charge', color: '#1565c0' },
};
const ANOMALY_FLAGS = ['LARGE', 'DUPLICATE', 'MERCHANT_SPIKE'];

export default function ReceiptsTab() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');       // '' = let the server pick the latest
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('attention');
  const [employee, setEmployee] = useState('');   // '' = every card member
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [reasonEdit, setReasonEdit] = useState({});   // receiptId -> draft text
  const [attachFor, setAttachFor] = useState('');     // txnId currently picking a receipt
  const [photo, setPhoto] = useState(null);           // { url } modal
  const [downloading, setDownloading] = useState(false);
  const [showStaff, setShowStaff] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      const target = m != null ? m : month;
      if (target) params.month = target;
      const res = await adminApi.reconcileReceipts(params);
      setData(res);
      // First load with no month picked: adopt the latest available month so the
      // picker and the data agree.
      if (!target && res.availableMonths && res.availableMonths.length) {
        setMonth(res.availableMonths[0]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  // Load once on mount; month changes reload explicitly via onPickMonth.
  useEffect(() => { load(); }, []);

  // If the picked employee has no charges in the freshly loaded month, drop back
  // to all employees so the table isn't mysteriously empty.
  useEffect(() => {
    if (!employee || !data) return;
    const present = (data.rows || []).some(r => r.type === 'charge' && (r.cardMember || '').trim() === employee);
    if (!present) setEmployee('');
  }, [data, employee]);

  async function onPickMonth(m) {
    setMonth(m);
    await load(m);
  }

  async function onUploadCsv(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const text = await fileToCsv(file);
      const res = await adminApi.importAmex(text);
      const landed = (res.months && res.months[res.months.length - 1]) || '';
      let note = `Imported ${res.imported} new transaction${res.imported === 1 ? '' : 's'}`;
      const extras = [];
      if (res.duplicates) extras.push(`${res.duplicates} already on file`);
      if (res.skipped) extras.push(`${res.skipped} non-transaction row${res.skipped === 1 ? '' : 's'} skipped`);
      if (extras.length) note += ` (${extras.join(', ')})`;
      if (landed) { setMonth(landed); await load(landed); }
      else await load();
      alert(note + '.');
    } catch (err) {
      setError('Import failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  }

  async function viewPhoto(receiptId) {
    try {
      const res = await adminApi.getReceiptPhoto(receiptId);
      setPhoto({ url: res.url });
    } catch (e) {
      alert('Could not load photo: ' + e.message);
    }
  }

  async function saveReason(receiptId) {
    const text = (reasonEdit[receiptId] || '').trim();
    if (!text) return;
    setBusyId(receiptId);
    try {
      await adminApi.setReceiptReason(receiptId, text);
      setReasonEdit(prev => { const { [receiptId]: _, ...rest } = prev; return rest; });
      await load();
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setBusyId('');
    }
  }

  async function attachReceipt(txnId, receiptId) {
    setBusyId(txnId);
    try {
      await adminApi.matchReceipt(receiptId, txnId);
      setAttachFor('');
      await load();
    } catch (e) {
      alert('Attach failed: ' + e.message);
    } finally {
      setBusyId('');
    }
  }

  async function unmatch(receiptId) {
    setBusyId(receiptId);
    try {
      await adminApi.matchReceipt(receiptId, null);
      await load();
    } catch (e) {
      alert('Unmatch failed: ' + e.message);
    } finally {
      setBusyId('');
    }
  }

  async function downloadReport() {
    setDownloading(true);
    try {
      const params = month ? { month } : {};
      const csvText = await adminApi.receiptReport(params);
      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `amex-reconciliation-${month || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setDownloading(false);
    }
  }

  if (loading && !data) return <p style={s.sub}>Loading receipts…</p>;
  if (error && !data) return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <p style={s.errorText}>{error}</p>
      <button style={{ ...s.btnPrimary, marginTop: '1rem' }} onClick={() => load()}>Retry</button>
    </div>
  );

  const rows = data?.rows || [];
  const allChargeRows = rows.filter(r => r.type === 'charge');
  const orphanRows = rows.filter(r => r.type === 'orphan_receipt');
  const availableMonths = data?.availableMonths || [];
  const summary = data?.summary || {};

  // Distinct card members present this month, for the employee picker. AMEX tags
  // each charge with the cardholder, so this is "whose charges are these" — the
  // right axis for "did Courtney upload all her receipts", since a missing
  // receipt still carries a card member (an unmatched-receipt filter never would).
  const cardMembers = [...new Set(allChargeRows.map(r => (r.cardMember || '').trim()).filter(Boolean))].sort();
  const hasEmployeeFilter = cardMembers.length > 1;

  // Scope every downstream count and the table to the picked employee. Left as
  // all charges when none is picked (or the AMEX export carried no card member).
  const chargeRows = employee ? allChargeRows.filter(r => (r.cardMember || '').trim() === employee) : allChargeRows;

  // Anomalies counted as distinct charge rows carrying any anomaly flag, so the
  // three overlapping rules (large / dup / spike) don't triple-count one charge.
  // Anomalies feed the month-overview KPI row, so it stays month-wide (global);
  // the rest below scope to the picked employee to match the table under them.
  const anomalyCount = allChargeRows.filter(r => r.flags.some(f => ANOMALY_FLAGS.includes(f))).length;
  const attentionCount = chargeRows.filter(r => r.flags.length > 0).length;
  const missingCount = chargeRows.filter(r => r.flags.includes('MISSING_RECEIPT')).length;
  const noReasonCount = chargeRows.filter(r => r.flags.includes('NO_REASON')).length;

  // Empty state — nothing imported yet.
  if (availableMonths.length === 0 && chargeRows.length === 0 && orphanRows.length === 0) {
    return (
      <div>
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <h3 style={{ ...s.sectionTitle, marginBottom: '0.5rem' }}>No receipts yet</h3>
          <p style={s.sub}>
            Upload a month's AMEX export (CSV or Excel) to start reconciling. Field staff capture receipts at{' '}
            <a href="https://receipts.silverservice.com" target="_blank" rel="noreferrer" style={{ color: '#7B1E3A', fontWeight: 600 }}>
              receipts.silverservice.com
            </a>.
          </p>
          <label style={{ ...s.btnPrimary, marginTop: '1.25rem', display: 'inline-block', cursor: 'pointer' }}>
            {uploading ? 'Importing…' : 'Upload AMEX file'}
            <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onUploadCsv} disabled={uploading} style={{ display: 'none' }} />
          </label>
          {error && <p style={{ ...s.errorText, marginTop: '1rem' }}>{error}</p>}
        </div>
        <StaffSection show={showStaff} onToggle={() => setShowStaff(v => !v)} />
      </div>
    );
  }

  const visibleCharges = chargeRows.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'attention') return r.flags.length > 0;
    if (filter === 'missing') return r.flags.includes('MISSING_RECEIPT');
    if (filter === 'noreason') return r.flags.includes('NO_REASON');
    if (filter === 'anomaly') return r.flags.some(f => ANOMALY_FLAGS.includes(f));
    return true;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.9rem', color: '#555', fontWeight: 600 }}>Statement month</span>
          <select style={s.select} value={month} onChange={e => onPickMonth(e.target.value)}>
            {availableMonths.map(m => <option key={m} value={m}>{prettyMonth(m)}</option>)}
            <option value="">All months</option>
          </select>
          {hasEmployeeFilter && (
            <>
              <span style={{ fontSize: '0.9rem', color: '#555', fontWeight: 600, marginLeft: '0.4rem' }}>Employee</span>
              <select style={s.select} value={employee} onChange={e => setEmployee(e.target.value)}>
                <option value="">All employees</option>
                {cardMembers.map(name => <option key={name} value={name}>{name}</option>)}
              </select>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <label style={{ ...s.btnOutline, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
            {uploading ? 'Importing…' : 'Upload AMEX file'}
            <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onUploadCsv} disabled={uploading} style={{ display: 'none' }} />
          </label>
          <button style={s.btnPrimary} onClick={downloadReport} disabled={downloading || chargeRows.length === 0}>
            {downloading ? 'Downloading…' : 'Download report'}
          </button>
        </div>
      </div>

      {error && <div style={s.errorBanner}>{error}</div>}

      <div style={s.kpiGrid}>
        <Kpi label="Charges" value={summary.chargeCount || 0} hint={fmt$(((summary.totalChargedCents || 0) / 100))} />
        <Kpi label="Matched" value={summary.matchedCount || 0} color="#2e7d32" />
        <Kpi label="Missing receipt" value={summary.missingReceiptCount || 0} color={summary.missingReceiptCount ? '#c62828' : '#333'} />
        <Kpi label="No reason" value={summary.noReasonCount || 0} color={summary.noReasonCount ? '#b26500' : '#333'} />
        <Kpi label="Anomalies" value={anomalyCount} color={anomalyCount ? '#ef6c00' : '#333'} />
      </div>

      {employee && (
        <div style={missingCount ? s.employeeBannerWarn : s.employeeBannerOk}>
          <strong>{employee}</strong> · {chargeRows.length} charge{chargeRows.length === 1 ? '' : 's'} this month ·{' '}
          {missingCount
            ? `${missingCount} missing receipt${missingCount === 1 ? '' : 's'}`
            : 'every charge has a receipt'}
          {noReasonCount ? ` · ${noReasonCount} with no reason` : ''}
        </div>
      )}

      <div style={s.rangeBar}>
        {[
          ['attention', `Needs attention · ${attentionCount}`],
          ['all', 'All'],
          ['missing', 'Missing receipt'],
          ['noreason', 'No reason'],
          ['anomaly', 'Anomalies'],
        ].map(([key, label]) => (
          <button key={key} style={filter === key ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setFilter(key)}>
            {label}
          </button>
        ))}
        <span style={s.totalLabel}>{visibleCharges.length} of {chargeRows.length}</span>
      </div>

      <div style={{ ...s.section, overflowX: 'auto' }}>
        <table style={{ ...s.table, minWidth: 760 }}>
          <thead>
            <tr>
              <th style={s.th}>Date</th>
              <th style={s.th}>Merchant</th>
              <th style={s.thR}>Amount</th>
              <th style={s.th}>Receipt / reason</th>
              <th style={s.th}>Status</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {visibleCharges.map((r, i) => {
              const editing = reasonEdit[r.receiptId] != null && r.receiptId;
              return (
                <Fragment key={r.txnId || i}>
                  <tr>
                    <td style={s.td}>{shortDate(r.date)}</td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 500 }}>{r.description || '—'}</div>
                      {r.cardMember && <div style={s.muted}>{r.cardMember}</div>}
                    </td>
                    <td style={{ ...s.tdR, fontWeight: 500 }}>{r.amount}</td>
                    <td style={s.td}>
                      {r.receiptId ? (
                        <div>
                          <span style={{ color: r.reason ? '#444' : '#b26500' }}>{r.reason || '— no reason —'}</span>
                          {r.capturedBy && <span style={s.muted}> · {r.capturedBy}</span>}
                          {r.manualMatch && <span style={s.manualTag}>manual</span>}
                          {r.matchNote && <div style={{ fontSize: '0.72rem', color: '#888', fontStyle: 'italic', marginTop: 1 }}>{r.matchNote}</div>}
                        </div>
                      ) : <span style={s.muted}>—</span>}
                      {editing && (
                        <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
                          <input
                            style={{ ...s.smallInput, width: 220 }} autoFocus
                            placeholder="Reason / business purpose"
                            value={reasonEdit[r.receiptId]}
                            onChange={e => setReasonEdit(p => ({ ...p, [r.receiptId]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && saveReason(r.receiptId)}
                          />
                          <button style={s.btnSmall} onClick={() => saveReason(r.receiptId)} disabled={busyId === r.receiptId}>Save</button>
                          <button style={s.btnGhost} onClick={() => setReasonEdit(p => { const { [r.receiptId]: _, ...rest } = p; return rest; })}>Cancel</button>
                        </div>
                      )}
                      {attachFor === r.txnId && (
                        <AttachPicker
                          orphans={orphanRows}
                          busy={busyId === r.txnId}
                          onPick={(rid) => attachReceipt(r.txnId, rid)}
                          onCancel={() => setAttachFor('')}
                        />
                      )}
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {r.flags.length === 0 && <FlagPill flag="OK" />}
                        {r.flags.map(f => <FlagPill key={f} flag={f} title={f === 'MERCHANT_SPIKE' && r.spikeMultiple ? `${r.spikeMultiple}× this merchant's usual (${r.spikeMedian})` : undefined} />)}
                      </div>
                    </td>
                    <td style={s.tdActions}>
                      {r.flags.includes('MISSING_RECEIPT') && (
                        <button style={s.btnSmall} onClick={() => setAttachFor(attachFor === r.txnId ? '' : r.txnId)} disabled={orphanRows.length === 0} title={orphanRows.length === 0 ? 'No unmatched receipts to attach' : 'Attach an existing receipt'}>
                          Attach
                        </button>
                      )}
                      {r.receiptId && r.flags.includes('NO_REASON') && !editing && (
                        <button style={s.btnSmall} onClick={() => setReasonEdit(p => ({ ...p, [r.receiptId]: '' }))}>Add reason</button>
                      )}
                      {r.receiptId && (
                        <button style={s.btnGhost} onClick={() => viewPhoto(r.receiptId)}>View</button>
                      )}
                      {r.manualMatch && (
                        <button style={s.btnGhost} onClick={() => unmatch(r.receiptId)} disabled={busyId === r.receiptId}>Unmatch</button>
                      )}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
            {visibleCharges.length === 0 && (
              <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#888', padding: '1.5rem' }}>
                {filter === 'attention' ? 'Nothing needs attention — this month is clean.' : 'No charges match this filter.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {orphanRows.length > 0 && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Orphan receipts <span style={s.muted}>· uploaded, but no matching charge</span></h3>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Date</th>
                <th style={s.th}>Merchant / reason</th>
                <th style={s.thR}>Amount</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {orphanRows.map((r, i) => (
                <tr key={r.receiptId || i}>
                  <td style={s.td}>{shortDate(r.date)}</td>
                  <td style={s.td}>
                    <div>{r.description || '—'}</div>
                    <div style={{ color: r.reason ? '#666' : '#b26500', fontSize: '0.8rem' }}>{r.reason || '— no reason —'}{r.capturedBy ? ` · ${r.capturedBy}` : ''}</div>
                  </td>
                  <td style={s.tdR}>{r.amount || '—'}</td>
                  <td style={s.tdActions}>
                    <button style={s.btnGhost} onClick={() => viewPhoto(r.receiptId)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ ...s.muted, marginTop: '0.5rem' }}>To match one of these, find its charge above and click <strong>Attach</strong>.</p>
        </div>
      )}

      <StaffSection show={showStaff} onToggle={() => setShowStaff(v => !v)} />

      {photo && (
        <div style={s.modalBg} onClick={() => setPhoto(null)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontWeight: 600, color: '#333' }}>Receipt photo</span>
              <button style={s.btnGhost} onClick={() => setPhoto(null)}>Close</button>
            </div>
            <img src={photo.url} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8, display: 'block', margin: '0 auto' }} />
          </div>
        </div>
      )}
    </div>
  );
}

function AttachPicker({ orphans, busy, onPick, onCancel }) {
  const [sel, setSel] = useState('');
  return (
    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <select style={s.smallInput} value={sel} onChange={e => setSel(e.target.value)}>
        <option value="">Pick an unmatched receipt…</option>
        {orphans.map(o => (
          <option key={o.receiptId} value={o.receiptId}>
            {shortDate(o.date)} · {o.amount || '?'} · {o.reason || o.description || 'receipt'}
          </option>
        ))}
      </select>
      <button style={s.btnSmall} onClick={() => sel && onPick(sel)} disabled={!sel || busy}>{busy ? '…' : 'Attach'}</button>
      <button style={s.btnGhost} onClick={onCancel}>Cancel</button>
    </div>
  );
}

// ── Field-staff (capturer) management ──
function StaffSection({ show, onToggle }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setList((await adminApi.listCapturers()).capturers || []); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (show && list == null) load(); }, [show, list, load]);

  async function add() {
    setErr('');
    if (!form.name || !form.email || !form.password) { setErr('Name, email and password are all required.'); return; }
    setSaving(true);
    try {
      await adminApi.createCapturer(form);
      setForm({ name: '', email: '', password: '' });
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function remove(email) {
    if (!window.confirm(`Remove ${email}? They will no longer be able to capture receipts.`)) return;
    try { await adminApi.deleteCapturer(email); await load(); }
    catch (e) { alert('Remove failed: ' + e.message); }
  }

  return (
    <div style={{ ...s.section, marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={onToggle}>
        <h3 style={{ ...s.sectionTitle, margin: 0 }}>Field staff <span style={s.muted}>· who can capture receipts</span></h3>
        <span style={{ color: '#888' }}>{show ? '▾' : '▸'}</span>
      </div>
      {show && (
        <div style={{ marginTop: '1rem' }}>
          {loading && <p style={s.sub}>Loading…</p>}
          {list && list.length > 0 && (
            <table style={{ ...s.table, marginBottom: '1rem' }}>
              <thead><tr><th style={s.th}>Name</th><th style={s.th}>Email</th><th style={s.th}>Last login</th><th style={s.th}></th></tr></thead>
              <tbody>
                {list.map(c => (
                  <tr key={c.email}>
                    <td style={s.td}>{c.name || '—'}</td>
                    <td style={s.td}>{c.email}</td>
                    <td style={s.td}>{c.lastLoginAt ? shortDate(c.lastLoginAt.slice(0, 10)) : '—'}</td>
                    <td style={s.tdActions}><button style={s.btnGhost} onClick={() => remove(c.email)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {list && list.length === 0 && <p style={{ ...s.sub, marginBottom: '1rem' }}>No field-staff logins yet. Add one below.</p>}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input style={s.smallInput} placeholder="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <input style={s.smallInput} placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <input style={s.smallInput} type="password" placeholder="Temp password (8+ chars)" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            <button style={s.btnSmall} onClick={add} disabled={saving}>{saving ? 'Adding…' : 'Add staff login'}</button>
          </div>
          {err && <p style={{ ...s.errorText, marginTop: '0.5rem' }}>{err}</p>}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, color }) {
  return (
    <div style={s.kpiCard}>
      <div style={s.kpiLabel}>{label}</div>
      <div style={{ ...s.kpiValue, ...(color ? { color } : {}) }}>{value}</div>
      {hint && <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function FlagPill({ flag, title }) {
  if (flag === 'OK') {
    return <span style={{ ...pillBase, background: 'rgba(46,125,50,0.1)', color: '#2e7d32' }}>Matched</span>;
  }
  const meta = FLAG_META[flag] || { label: flag, color: '#888' };
  return <span title={title} style={{ ...pillBase, background: `${meta.color}15`, color: meta.color }}>{meta.label}</span>;
}

const pillBase = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' };

// Turn an uploaded AMEX file into CSV text for the import endpoint. A .csv is
// passed straight through; an Excel export is converted in the browser via
// SheetJS — loaded on demand with a dynamic import so the ~600KB library never
// ships in the customer bundle, only when an admin actually imports a file. We
// emit the whole transaction sheet (preamble rows and all) as clean CSV; the
// backend parser finds the real header row. Embedded newlines (AMEX's "Extended
// Details" cells) are flattened to spaces here so a multi-line cell can't get
// split across rows once the backend re-parses the CSV.
async function fileToCsv(file) {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.csv') || file.type === 'text/csv') return file.text();
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheetName = wb.SheetNames.find(n => /detail/i.test(n)) || wb.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, raw: false, blankrows: false });
  return rows.map(r => (Array.isArray(r) ? r : [r]).map(csvCell).join(',')).join('\n');
}
function csvCell(v) {
  const str = (v == null ? '' : String(v)).replace(/[\r\n]+/g, ' ').trim();
  return /[",]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function fmt$(v) { return '$' + (v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
function prettyMonth(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || 'All months';
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
function shortDate(d) {
  if (!d || !/^\d{4}-\d{2}-\d{2}/.test(d)) return d || '—';
  const [y, mo, day] = d.slice(0, 10).split('-');
  return new Date(Number(y), Number(mo) - 1, Number(day)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Style subset mirrors ReplenishmentReport.jsx so the tab renders identically to
// the rest of the admin portal without depending on Admin's internal `s`.
const s = {
  sub: { color: '#888', fontSize: '0.85rem', margin: 0 },
  muted: { color: '#888', fontSize: '0.8rem' },
  select: { padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.85rem', outline: 'none', background: '#fff' },
  btnPrimary: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 8, padding: '0.6rem 1.25rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.82rem', cursor: 'pointer' },
  btnSmall: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#777', border: '1px solid #ddd', borderRadius: 6, padding: '0.35rem 0.8rem', fontSize: '0.78rem', cursor: 'pointer' },
  rangeBar: { display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  rangeBtn: { padding: '0.4rem 0.9rem', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: '0.8rem', cursor: 'pointer', color: '#555', fontWeight: 500 },
  rangeBtnActive: { background: '#A50B18', color: '#fff', border: '1px solid #A50B18' },
  totalLabel: { marginLeft: 'auto', fontSize: '0.85rem', color: '#888', fontWeight: 500 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' },
  kpiCard: { background: '#fff', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  kpiLabel: { fontSize: '0.72rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' },
  kpiValue: { fontSize: '1.5rem', fontWeight: 700, color: '#333' },
  section: { background: '#fff', borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 600, color: '#333', margin: '0 0 1rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', fontSize: '0.72rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  thR: { textAlign: 'right', padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', fontSize: '0.72rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f5f5f5', color: '#444', verticalAlign: 'top' },
  tdR: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f5f5f5', color: '#444', textAlign: 'right', verticalAlign: 'top' },
  tdActions: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f5f5f5', textAlign: 'right', whiteSpace: 'nowrap', verticalAlign: 'top', display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' },
  smallInput: { padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' },
  errorText: { color: '#c62828', fontSize: '0.85rem' },
  errorBanner: { background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#a32d2d' },
  employeeBannerOk: { background: 'rgba(46,125,50,0.08)', border: '1px solid rgba(46,125,50,0.25)', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#2e7d32' },
  employeeBannerWarn: { background: 'rgba(198,40,40,0.06)', border: '1px solid rgba(198,40,40,0.25)', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#a32d2d' },
  manualTag: { marginLeft: 6, fontSize: '0.66rem', fontWeight: 700, color: '#1565c0', background: 'rgba(21,101,192,0.1)', padding: '1px 6px', borderRadius: 8 },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modalCard: { background: '#fff', borderRadius: 12, padding: '1.25rem', maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'auto' },
};
